import { resolveParameterDefault } from './parameterDefaults';
import { isReservedParameterName } from './reservedParameters';
const resolveDefinitionDefaults = (definitions, now, timezone) => Object.entries(definitions).reduce((acc, [key, definition]) => {
    const value = resolveParameterDefault(definition, now, timezone ?? undefined);
    return value === undefined ? acc : { ...acc, [key]: value };
}, {});
const withoutUndefinedValues = (values) => Object.fromEntries(Object.entries(values).filter(([, value]) => value !== undefined));
export const getEffectiveParameterDefinitions = ({ projectDefinitions, exploreDefinitions, }) => ({
    ...projectDefinitions,
    ...exploreDefinitions,
});
export const resolveFallbackParameterValues = ({ projectDefinitions, exploreDefinitions, virtualViewSavedValues, now, timezone, }) => ({
    ...resolveDefinitionDefaults(projectDefinitions, now, timezone),
    ...resolveDefinitionDefaults(exploreDefinitions, now, timezone),
    ...withoutUndefinedValues(virtualViewSavedValues),
});
// A chart's saved value applies only when the dashboard has no value and the definition no default
export const canUseChartSavedParameterValue = ({ key, dashboardValues, definitions, }) => dashboardValues[key] === undefined &&
    definitions[key]?.default === undefined;
// Targeted: dashboard value, else chart-saved only when the definition has no default.
// Untargeted: chart-saved values only.
export const getDashboardTileParameterOverrides = ({ definitions, dashboardValues, chartSavedValues, isTargeted, }) => {
    if (!isTargeted)
        return withoutUndefinedValues(chartSavedValues);
    const keys = new Set([
        ...Object.keys(chartSavedValues),
        ...Object.keys(dashboardValues),
    ]);
    return [...keys].reduce((acc, key) => {
        const dashboardValue = dashboardValues[key];
        if (dashboardValue !== undefined) {
            return { ...acc, [key]: dashboardValue };
        }
        const chartSavedValue = chartSavedValues[key];
        if (chartSavedValue === undefined ||
            !canUseChartSavedParameterValue({
                key,
                dashboardValues,
                definitions,
            })) {
            return acc;
        }
        return { ...acc, [key]: chartSavedValue };
    }, {});
};
export const resolveDashboardTileParameters = ({ fallbackSources, ...inputs }) => ({
    ...resolveFallbackParameterValues(fallbackSources),
    ...getDashboardTileParameterOverrides({
        ...inputs,
        definitions: getEffectiveParameterDefinitions(fallbackSources),
    }),
});
export var DashboardTileParameterSource;
(function (DashboardTileParameterSource) {
    DashboardTileParameterSource["DASHBOARD"] = "dashboard";
    DashboardTileParameterSource["CHART"] = "chart";
    DashboardTileParameterSource["DEFAULT"] = "default";
})(DashboardTileParameterSource || (DashboardTileParameterSource = {}));
// Where a tile's value for one parameter comes from; default covers the whole fallback chain.
export const getDashboardTileParameterSource = ({ key, ...inputs }) => {
    if (getDashboardTileParameterOverrides(inputs)[key] === undefined) {
        return DashboardTileParameterSource.DEFAULT;
    }
    return inputs.isTargeted && inputs.dashboardValues[key] !== undefined
        ? DashboardTileParameterSource.DASHBOARD
        : DashboardTileParameterSource.CHART;
};
// Referenced keys with no dashboard value and no definition default, paired with
// whether each referencing tile has a chart-saved value for them.
const getKeysResolvedPerTile = ({ tiles, dashboardValues, definitions, }) => tiles.reduce((acc, { parameterReferences, chartSavedValues }) => {
    new Set(parameterReferences).forEach((key) => {
        if (isReservedParameterName(key) ||
            !canUseChartSavedParameterValue({
                key,
                dashboardValues,
                definitions,
            })) {
            return;
        }
        acc.set(key, [
            ...(acc.get(key) ?? []),
            chartSavedValues[key] !== undefined,
        ]);
    });
    return acc;
}, new Map());
// A key is missing when any referencing tile has no dashboard value, default or chart-saved value.
export const getMissingRequiredDashboardParameters = (inputs) => [...getKeysResolvedPerTile(inputs)]
    .filter(([, hasChartValues]) => hasChartValues.some((has) => !has))
    .map(([key]) => key);
//# sourceMappingURL=dashboardTileParameters.js.map